import React, {Children, useEffect, useRef, useState} from "react";
import css from "./FilterBar.module.scss";
import Button from "../../Button/Button.jsx";
import PropTypes from "prop-types";
import Download02 from "../../Icons/Download02.jsx";
import Filter from "../../Icons/Filter.jsx";
import ArrowRefresh01 from "../../Icons/ArrowRefresh01.jsx";
import AppliedFilters from "./AppliedFilters.jsx";
import FiltersSidePanel from "./FiltersSidePanel.jsx";
import FilterBarItem from "./FilterBarItem.jsx";
import FilterBarPillButton from "./FilterBarPillButton.jsx";
import ColumnHorizontal01Icon from "../../Icons/ColumnHorizontal01Icon.jsx";
import MultiSelect from "../../FormElements/MultiSelect/MultiSelect.jsx";
import {decodeFilters, encodeFilters} from "../../../utils/filterUrlEncoding.js";
import {useColumnVisibilityContext} from "../../../providers/ColumnVisibilityProvider.jsx";

const FiltersBarWithSidePanel = ({
  children,
  onChange,
  onApply,
  onUpdate,
  onPdfExport,
  onCsvExport,
  onPrint,
  onExportAll,
  onRefresh,
  className,
  trackingContext,
  posthog,
  externalAppliedFilters,
  onUrlStateChange,
  alwaysShowPanel = false,
  forceInline = false,
}) => {
  const childrenArray = Children.toArray(children);
  const availableFilterItems = childrenArray.filter(child => child.type && child.type === FilterBarItem)
  const pillButtons = childrenArray.filter(child => child.type && (child.type === FilterBarPillButton || child.type.__IS_FILTER_BAR_PILL_BUTTON))
  const getDefaultValues = (items) => {
    return items.reduce((acc, child) => {
        if (child.props.defaultValue) {
          acc[child.props.name] = child.props.defaultValue;
        }

        return acc;
      }, {});
  }

  const retrieveFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const filters = urlParams.get('filters');

    return filters ? decodeFilters(filters) : null;
  }

  const initFilters = (items) => {
    const defaultValues = getDefaultValues(items);
    const filtersFromUrl = retrieveFiltersFromUrl();

    return filtersFromUrl ? {...defaultValues, ...filtersFromUrl} : defaultValues;
  }

  const notifyUrlStateChange = (filters) => {
    if (!onUrlStateChange) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set('filters', encodeFilters(filters));
    onUrlStateChange(params);
  };

  const [appliedFilters, setAppliedFilters] = useState(initFilters(availableFilterItems));
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportBtnRef = useRef();
  const [panelFilters, setPanelFilters] = useState(appliedFilters);

  const capture = (action, properties = null) => {
    if (trackingContext) {
      posthog.capture(trackingContext+':filters:'+action, properties)
    }
  };

  const openFiltersPanel = () => {
    setFiltersPanelOpen(true);
    setPanelFilters(appliedFilters);
    capture('panel_open')
  };

  const handleChange = (key, value) => {
    if (filtersPanelOpen) {
      let nextDraft = {...panelFilters, [key]: value};
      if (onUpdate) {
        nextDraft = onUpdate(key, value, {...panelFilters});
      }
      setPanelFilters(nextDraft);
      // If onUpdate is provided is enabled, call onChange so parent can react and update filter options
      if (onUpdate) {
        onChange(nextDraft);
      }
      return;
    }

    const next = {...appliedFilters, [key]: value};
    setAppliedFilters(next);
    onChange(next);
    notifyUrlStateChange(next);
  };

  const handleApply = () => {
    capture('apply')
    const nextApplied = filtersPanelOpen ? panelFilters : appliedFilters;
    setAppliedFilters(nextApplied);
    if (onApply) {
      onApply(nextApplied);
    } else {
      onChange(nextApplied);
    }
    setFiltersPanelOpen(false);
    notifyUrlStateChange(nextApplied);
  };

  const handleClearAll = () => {
    capture('clear_all')
    setAppliedFilters({})
    onChange({})
    notifyUrlStateChange({})
  }

  const handleRemove = (filterName) => {
    capture('remove', {
      filter: filterName,
    })
    const newFilters = {...appliedFilters};
    delete newFilters[filterName];
    setAppliedFilters(newFilters);
    onChange(newFilters);
    notifyUrlStateChange(newFilters);
  };

  const handleAppliedFilterChipDropdownChange = (filterName, value) => {
    capture('inline_change', {
      filter: filterName,
    })
    const newFilters = {...appliedFilters};
    newFilters[filterName] = value
    setAppliedFilters(newFilters);
    onChange(newFilters);
    notifyUrlStateChange(newFilters);
  };

  const handleExport = (format) => {
    capture('export', {
      format: format,
    })

    setExportOpen(false);
    if (format === 'pdf' && onPdfExport) onPdfExport();
    if (format === 'csv' && onCsvExport) onCsvExport();
    if (format === 'print' && onPrint) onPrint();
    if (format === 'exportAll' && onExportAll) onExportAll();
  };

  useEffect(() => {
    if (!exportOpen) return;
    const handle = (e) => {
      if (exportBtnRef.current && !exportBtnRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [exportOpen]);

  const showInlineInputs = !alwaysShowPanel && (forceInline || Children.count(children) <= 1);
  const exportOptions = [
    onPdfExport && {value: 'pdf', label: 'PDF'},
    onCsvExport && {value: 'csv', label: 'CSV'},
    onPrint && {value: 'print', label: 'Print'},
    onExportAll && {value: 'exportAll', label: 'Export All'},
  ].filter(Boolean);

  useEffect(() => {
    // Only proceed if externalAppliedFilters is valid
    if (externalAppliedFilters) {
      const updatedFilters = { ...appliedFilters };
      let changed = false;

      Object.keys(externalAppliedFilters).forEach((key) => {
        if (
          externalAppliedFilters[key] !== undefined &&
          externalAppliedFilters[key] !== appliedFilters[key]
        ) {
          updatedFilters[key] = externalAppliedFilters[key];
          changed = true;
        }
      });

      if (changed) {
        setAppliedFilters(updatedFilters);
        notifyUrlStateChange(updatedFilters);
      }
    }
  }, [externalAppliedFilters, appliedFilters, notifyUrlStateChange]);

  useEffect(() => {
    onChange(appliedFilters);
  }, [])

  // Keep side panel filters in sync with applied filters when panel is open
  useEffect(() => {
    if (filtersPanelOpen) {
      setPanelFilters(appliedFilters);
    }
  }, [appliedFilters, filtersPanelOpen])

  const renderedPills = pillButtons.length > 0 ? (
    <div style={{display: 'flex', gap: '8px'}}>
      {pillButtons.map((pill, idx) =>
        React.cloneElement(pill, {key: idx}))
      }
    </div>
  ) : null;

  return (
    <div className={className}>
      <FiltersBar
        showInlineInputs={showInlineInputs}
        availableFilterItems={availableFilterItems}
        appliedFilters={appliedFilters}
        onChange={handleChange}
        onRemove={handleRemove}
        onClearAll={handleClearAll}
        onDropdownChange={handleAppliedFilterChipDropdownChange}
        openFiltersPanel={openFiltersPanel}
        exportOptions={exportOptions}
        handleExportOption={handleExport}
        onAppliedFilterChipClick={() => setFiltersPanelOpen(true)}
        quickFilters={renderedPills}
        onRefresh={onRefresh}
      />
      <FiltersSidePanel
        showInlineInputs={showInlineInputs}
        filtersPanelOpen={filtersPanelOpen}
        availableFilterItems={availableFilterItems}
        appliedFilters={panelFilters}
        onChange={handleChange}
        onClose={() => setFiltersPanelOpen(false)}
        onApply={handleApply}
        quickFilters={renderedPills}
      />
    </div>
  );
};

const FiltersBar = ({
  showInlineInputs,
  availableFilterItems,
  appliedFilters,
  onChange,
  onRemove,
  onClearAll,
  onDropdownChange,
  openFiltersPanel,
  exportOptions,
  handleExportOption,
  onAppliedFilterChipClick,
  quickFilters,
  onRefresh,
}) => {
  return (
    <div className={`${css.filtersBar} ${showInlineInputs && css.inlineInputsFiltersBar}`}>
      {quickFilters}
      {quickFilters && (
        <span
          aria-hidden
          style={{
            width: '1px',
            alignSelf: 'stretch',
            background: 'var(--grey-12, #e5e7eb)',
            margin: '0 12px',
            display: 'inline-block',
            borderRadius: '1px',
            opacity: 0.9,
          }}
        />
      )}
      <AppliedFilters
        showInlineInputs={showInlineInputs}
        availableFilterItems={availableFilterItems}
        appliedFilters={appliedFilters}
        onChange={onChange}
        onRemove={onRemove}
        onClearAll={onClearAll}
        onDropdownChange={onDropdownChange}
        onAppliedFilterChipClick={onAppliedFilterChipClick}
      />
      <FilterButtonsSection
        showInlineInputs={showInlineInputs}
        openFiltersPanel={openFiltersPanel}
        exportOptions={exportOptions}
        handleExportOption={handleExportOption}
        onRefresh={onRefresh}
      />
    </div>
  );
};

const FilterButtonsSection = ({
  showInlineInputs,
  openFiltersPanel,
  exportOptions,
  handleExportOption,
  onRefresh,
}) => {
  return (
    <div className={`${css.buttonsSection} ${css.noPrint}`}>
      {!showInlineInputs &&
        <Button size="small" onClick={openFiltersPanel} icon={<Filter />}>
          Filters
        </Button>
      }
      <ColumnVisibility />
      {exportOptions.length > 0 &&
        <ExportDropdown
          options={exportOptions}
          onSelect={handleExportOption}
        />
      }
      {onRefresh &&
        <Button
          size="small"
          icon={<ArrowRefresh01 />}
          onClick={onRefresh}
          variant="tertiary"
        />
      }
    </div>
  );
};

const ExportDropdown = ({options, onSelect}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={ref}>
      <Button
        size="small"
        variant="tertiary"
        onClick={() => setOpen((prev) => !prev)}
      >
        <Download02 color="var(--grey-64)"/>
      </Button>
      {open && (
        <div className={css.exportDropdownMenu} role="listbox">
          {options.map((opt) =>
            <button
              key={opt.value}
              className={css.exportDropdownItem}
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ColumnVisibility = () => {
  const {visibleColumns, applyColumns, columnsVisibilityOptions} = useColumnVisibilityContext();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const [selectedColumns, setSelectedColumns] = useState(visibleColumns);

  useEffect(() => {
    setSelectedColumns(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!columnsVisibilityOptions?.length) {
    return null;
  }

  const onConfirmColumnVisibility = () => {
    applyColumns(selectedColumns);
    setOpen(false);
  }

  const handleSelectAllColumns = () => {
    setSelectedColumns(columnsVisibilityOptions.map(opt => opt.value));
  }

  return (
    <div ref={ref}>
      <Button
        size="small"
        variant="tertiary"
        onClick={() => setOpen((prev) => !prev)}
      >
        <ColumnHorizontal01Icon color="var(--grey-64)"/>
      </Button>
      {open &&
        <div className={css.columnsVisibilityMenu}>
          <MultiSelect
            label="Columns to show:"
            onChange={(evt) => setSelectedColumns(evt.target.value)}
            value={selectedColumns}
            options={columnsVisibilityOptions}
            onSelectAll={handleSelectAllColumns}
          />
          <Button
            variant="primary"
            size="small"
            onClick={onConfirmColumnVisibility}
          >
            Confirm
          </Button>
        </div>
      }
    </div>
  );
};

FiltersBarWithSidePanel.propTypes = {
  children: PropTypes.node.isRequired,
  appliedFilters: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
      PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
      }),
    ])
  ),
  onChange: PropTypes.func,
  onPdfExport: PropTypes.func,
  onCsvExport: PropTypes.func,
  onPrint: PropTypes.func,
  onExportAll: PropTypes.func,
  onRefresh: PropTypes.func,
  className: PropTypes.string,
  onUrlStateChange: PropTypes.func,
  alwaysShowPanel: PropTypes.bool,
  forceInline: PropTypes.bool,
};

export default FiltersBarWithSidePanel;
